import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Bell, BellRing, Plus, Trash2, Clock, Pill,
  AlarmClock, X, Volume2, RefreshCw
} from 'lucide-react';
import Layout from '../components/Layout';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RemindersPage = () => {
  const { t, language, speak } = useLanguage();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeReminder, setActiveReminder] = useState(null);
  const audioRef = useRef(null);
  const checkIntervalRef = useRef(null);

  const [formData, setFormData] = useState({
    medicine_name: '',
    reminder_time: '',
    notes: ''
  });

  // Fetch reminders
  const fetchReminders = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/reminders`);
      setReminders(response.data.reminders || []);
    } catch (error) {
      console.error('Error fetching reminders:', error);
      toast.error(language === 'hi' ? 'रिमाइंडर लोड करने में त्रुटि' : 'Failed to load reminders');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  // Check for active reminders every minute
  useEffect(() => {
    const checkReminders = () => {
      const now = new Date();
      const currentTime = now.toTimeString().slice(0, 5); // HH:MM

      reminders.forEach(reminder => {
        if (reminder.enabled && reminder.reminder_time === currentTime) {
          // Check if snoozed
          if (reminder.snoozed_until) {
            const snoozeTime = new Date(reminder.snoozed_until);
            if (now < snoozeTime) return; // Still snoozed
          }
          
          // Trigger reminder
          triggerReminder(reminder);
        }
      });
    };

    // Check immediately and then every 30 seconds
    checkReminders();
    checkIntervalRef.current = setInterval(checkReminders, 30000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [reminders]);

  // Trigger reminder notification
  const triggerReminder = (reminder) => {
    setActiveReminder(reminder);
    
    // Play sound (use browser notification sound or beep)
    try {
      // Create a simple beep using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
      }, 500);
    } catch (e) {
      console.log('Audio not available');
    }

    // Browser notification if permitted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(language === 'hi' ? 'दवाई का समय!' : 'Time to take medication!', {
        body: reminder.medicine_name,
        icon: '/icons/icon-192x192.png',
        tag: reminder.id
      });
    }

    // Text to speech
    const message = language === 'hi' 
      ? `दवाई का समय! ${reminder.medicine_name} लेने का समय हो गया है।`
      : `Time to take medication! Please take ${reminder.medicine_name}.`;
    speak(message);
  };

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Handle snooze
  const handleSnooze = async (minutes = 10) => {
    if (!activeReminder) return;

    try {
      await axios.post(`${API_URL}/reminders/${activeReminder.id}/snooze?minutes=${minutes}`);
      toast.success(language === 'hi' ? `${minutes} मिनट के लिए स्नूज़ किया` : `Snoozed for ${minutes} minutes`);
      setActiveReminder(null);
      fetchReminders();
    } catch (error) {
      toast.error('Failed to snooze');
    }
  };

  // Handle stop
  const handleStop = async () => {
    if (!activeReminder) return;

    try {
      await axios.post(`${API_URL}/reminders/${activeReminder.id}/stop`);
      toast.success(language === 'hi' ? 'रिमाइंडर बंद किया' : 'Reminder stopped');
      setActiveReminder(null);
      fetchReminders();
    } catch (error) {
      toast.error('Failed to stop');
    }
  };

  // Handle add reminder
  const handleAddReminder = async (e) => {
    e.preventDefault();
    if (!formData.medicine_name || !formData.reminder_time) {
      toast.error(language === 'hi' ? 'दवाई का नाम और समय जरूरी है' : 'Medicine name and time required');
      return;
    }

    try {
      await axios.post(`${API_URL}/reminders`, {
        medicine_name: formData.medicine_name,
        reminder_time: formData.reminder_time,
        notes: formData.notes || null,
        enabled: true
      });
      toast.success(language === 'hi' ? 'रिमाइंडर जोड़ा गया' : 'Reminder added');
      setFormData({ medicine_name: '', reminder_time: '', notes: '' });
      setShowAddForm(false);
      fetchReminders();
    } catch (error) {
      toast.error('Failed to add reminder');
    }
  };

  // Toggle reminder enabled/disabled
  const toggleReminder = async (reminder) => {
    try {
      await axios.put(`${API_URL}/reminders/${reminder.id}`, {
        enabled: !reminder.enabled
      });
      fetchReminders();
    } catch (error) {
      toast.error('Failed to update reminder');
    }
  };

  // Delete reminder
  const deleteReminder = async (id) => {
    try {
      await axios.delete(`${API_URL}/reminders/${id}`);
      toast.success(language === 'hi' ? 'रिमाइंडर हटाया गया' : 'Reminder deleted');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  // Format time for display
  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-[#0D2340]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 pb-nav">
        {/* Active Reminder Alert */}
        {activeReminder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md animate-pulse-slow bg-white shadow-2xl" data-testid="active-reminder-modal">
              <CardContent className="p-6 text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#FFB300] flex items-center justify-center animate-bounce">
                  <BellRing className="h-10 w-10 text-slate-900" />
                </div>
                <h2 className="text-2xl font-bold text-[#0D2340] mb-2 font-['Manrope']">
                  {language === 'hi' ? 'दवाई का समय!' : 'Time to take medication!'}
                </h2>
                <p className="text-xl font-semibold text-slate-700 mb-2">
                  {activeReminder.medicine_name}
                </p>
                {activeReminder.notes && (
                  <p className="text-slate-500 mb-4">{activeReminder.notes}</p>
                )}
                
                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={() => handleSnooze(10)}
                    variant="outline"
                    className="flex-1 h-14 text-lg border-[#0D2340] text-[#0D2340]"
                    data-testid="snooze-btn"
                  >
                    <AlarmClock className="h-5 w-5 mr-2" />
                    {language === 'hi' ? 'स्नूज़ (10 मि.)' : 'Snooze (10 min)'}
                  </Button>
                  <Button
                    onClick={handleStop}
                    className="flex-1 h-14 text-lg bg-[#2E7D32] hover:bg-[#2E7D32]/90"
                    data-testid="stop-btn"
                  >
                    <X className="h-5 w-5 mr-2" />
                    {language === 'hi' ? 'बंद करें' : 'Stop'}
                  </Button>
                </div>
                
                <div className="flex gap-2 mt-3">
                  <Button
                    onClick={() => handleSnooze(5)}
                    variant="ghost"
                    size="sm"
                    className="flex-1"
                  >
                    5 min
                  </Button>
                  <Button
                    onClick={() => handleSnooze(15)}
                    variant="ghost"
                    size="sm"
                    className="flex-1"
                  >
                    15 min
                  </Button>
                  <Button
                    onClick={() => handleSnooze(30)}
                    variant="ghost"
                    size="sm"
                    className="flex-1"
                  >
                    30 min
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0D2340] font-['Manrope'] flex items-center gap-3">
              <Bell className="h-8 w-8" />
              {language === 'hi' ? 'दवाई रिमाइंडर' : 'Medicine Reminders'}
            </h1>
            <p className="text-slate-600 mt-1">
              {language === 'hi' ? 'समय पर दवाई लेना न भूलें' : 'Never miss your medication'}
            </p>
          </div>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-[#0D2340]"
            data-testid="add-reminder-btn"
          >
            <Plus className="h-5 w-5 mr-2" />
            {language === 'hi' ? 'रिमाइंडर जोड़ें' : 'Add Reminder'}
          </Button>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <Card className="border-[#FFB300] bg-[#FFB300]/5" data-testid="add-reminder-form">
            <CardHeader>
              <CardTitle className="font-['Manrope']">
                {language === 'hi' ? 'नया रिमाइंडर' : 'New Reminder'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddReminder} className="space-y-4">
                <div>
                  <Label>{language === 'hi' ? 'दवाई का नाम' : 'Medicine Name'}</Label>
                  <Input
                    value={formData.medicine_name}
                    onChange={(e) => setFormData({ ...formData, medicine_name: e.target.value })}
                    placeholder={language === 'hi' ? 'जैसे: पैरासिटामोल' : 'e.g., Paracetamol'}
                    className="h-12"
                    data-testid="medicine-name-input"
                  />
                </div>
                <div>
                  <Label>{language === 'hi' ? 'समय' : 'Time'}</Label>
                  <Input
                    type="time"
                    value={formData.reminder_time}
                    onChange={(e) => setFormData({ ...formData, reminder_time: e.target.value })}
                    className="h-12 text-lg"
                    data-testid="reminder-time-input"
                  />
                </div>
                <div>
                  <Label>{language === 'hi' ? 'नोट (वैकल्पिक)' : 'Note (optional)'}</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={language === 'hi' ? 'जैसे: खाने के बाद' : 'e.g., After food'}
                    data-testid="reminder-notes-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="submit" className="flex-1 bg-[#2E7D32]" data-testid="save-reminder-btn">
                    {language === 'hi' ? 'सेव करें' : 'Save'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowAddForm(false)}
                    className="flex-1"
                  >
                    {language === 'hi' ? 'रद्द करें' : 'Cancel'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Reminders List */}
        {reminders.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center">
              <Bell className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 text-lg">
                {language === 'hi' ? 'कोई रिमाइंडर नहीं' : 'No reminders set'}
              </p>
              <p className="text-slate-400 text-sm mt-1">
                {language === 'hi' ? 'दवाई का रिमाइंडर जोड़ने के लिए ऊपर बटन दबाएं' : 'Tap the button above to add a medicine reminder'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {reminders.map((reminder) => (
              <Card 
                key={reminder.id} 
                className={`transition-all ${reminder.enabled ? 'border-l-4 border-l-[#2E7D32]' : 'opacity-60'}`}
                data-testid={`reminder-card-${reminder.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl ${reminder.enabled ? 'bg-[#2E7D32]/10' : 'bg-slate-100'}`}>
                      <Pill className={`h-6 w-6 ${reminder.enabled ? 'text-[#2E7D32]' : 'text-slate-400'}`} />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg font-['Manrope']">
                        {reminder.medicine_name}
                      </h3>
                      <div className="flex items-center gap-2 text-slate-500">
                        <Clock className="h-4 w-4" />
                        <span className="text-lg font-mono">{formatTime(reminder.reminder_time)}</span>
                      </div>
                      {reminder.notes && (
                        <p className="text-sm text-slate-400 mt-1">{reminder.notes}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <Switch
                        checked={reminder.enabled}
                        onCheckedChange={() => toggleReminder(reminder)}
                        data-testid={`toggle-reminder-${reminder.id}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => speak(`${reminder.medicine_name}, ${formatTime(reminder.reminder_time)}`)}
                      >
                        <Volume2 className="h-5 w-5 text-slate-400" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteReminder(reminder.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-reminder-${reminder.id}`}
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <BellRing className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">
                  {language === 'hi' ? 'रिमाइंडर कैसे काम करता है?' : 'How reminders work?'}
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  {language === 'hi' 
                    ? 'जब समय आएगा, आपको आवाज़ और नोटिफिकेशन से याद दिलाया जाएगा। आप स्नूज़ या बंद कर सकते हैं।'
                    : 'When the time comes, you will be reminded with sound and notification. You can snooze or stop.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default RemindersPage;
